import type { EmployeeRole } from "@care-by-ella/contracts";
import {
  clinicianMeResponseSchema,
  type ClinicianMeResponse,
} from "@care-by-ella/contracts";
import { API_BASE, tenantHeader, withCredentials } from "@/lib/api";

export interface InitResponse {
  orderRef: string;
  autoStartToken: string;
  qrStartToken: string;
  qrStartSecret: string;
}

export type PollStatus = "pending" | "failed" | "complete";

export interface PollResponse {
  status: PollStatus;
  hintCode?: string;
  jwt?: string;
  messageKey?: string;
}

export async function initClinicianBankId(): Promise<InitResponse> {
  const res = await fetch(`${API_BASE}/employee-api/v1/bankid/init`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...tenantHeader() },
  });
  if (!res.ok) throw new Error(`BankID init failed: ${res.status}`);
  return (await res.json()) as InitResponse;
}

export async function pollClinicianBankId(
  orderRef: string,
): Promise<PollResponse> {
  const res = await fetch(
    `${API_BASE}/employee-api/v1/bankid/${orderRef}/poll`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", ...tenantHeader() },
    },
  );
  if (!res.ok) throw new Error(`BankID poll failed: ${res.status}`);
  return (await res.json()) as PollResponse;
}

// CAR-36: claims the clinician "who am I" endpoint exposes. The JWT itself
// never reaches JS — these claims come from the API after it has verified the
// httpOnly session cookie. TES-281: the role union is the shared contracts
// vocabulary so it can never drift from the API's.
// An ALIAS, not a second declaration: this was a hand-maintained copy of the
// same shape the response is already parsed with two lines below, so adding a
// field to the contract left the admin unable to see it. That is precisely the
// drift `clinicianMeResponseSchema` was introduced to end.
export type ClinicianClaims = ClinicianMeResponse;

// Exchange the BankID-issued clinician JWT for an httpOnly session cookie.
// Idempotent server-side — safe to retry. The caller drops the JWT from JS
// memory immediately after this resolves.
export async function createClinicianSession(jwt: string): Promise<void> {
  const res = await fetch(`${API_BASE}/employee-api/v1/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...tenantHeader() },
    body: JSON.stringify({ jwt }),
    ...withCredentials,
  });
  if (!res.ok) throw new Error(`Session exchange failed: ${res.status}`);
}

// CAR-54 — /health reports `devAuthEnabled`; the login screen keys off it to
// decide whether to render the dev-login form. Returns null on any failure so
// the login screen safely falls back to BankID-only.
export interface PublicHealth {
  devAuthEnabled?: boolean;
}

export async function fetchPublicHealth(): Promise<PublicHealth | null> {
  try {
    // CAR-95: withCredentials carries `cache: "no-store"` (see lib/api.ts) —
    // load-bearing here. Pre-proxy, GET /api/health returned cacheable SPA HTML
    // that the browser kept serving from cache, so the dev-login form never
    // rendered. no-store forces a live request.
    const res = await fetch(`${API_BASE}/health`, { ...withCredentials });
    if (!res.ok) return null;
    return (await res.json()) as PublicHealth;
  } catch {
    return null;
  }
}

// CAR-54 — pre-BankID clinician dev-auth. Returns a doctor JWT identical to
// the BankID `poll`-complete response; the caller hands it to
// AuthContext.signIn so it goes through the same /session cookie exchange
// (CAR-36) and never lands in JS storage.
export interface DevAuthClinicianResponse {
  jwt: string;
  doctorId: string;
}

export class DevAuthError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "DevAuthError";
  }
}

// TES-494 — `role` is sent so the caller states which view it expects; the
// SERVER decides the role from the key that matched and rejects the request if
// the two disagree. It is a confirmation, never a request for privilege — a
// coach key with role:"admin" is refused, not quietly downgraded.
export async function devAuthClinician(
  doctorKey: string,
  role?: EmployeeRole,
): Promise<DevAuthClinicianResponse> {
  const res = await fetch(`${API_BASE}/employee-api/v1/dev-auth/clinician`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...tenantHeader() },
    body: JSON.stringify(role ? { doctorKey, role } : { doctorKey }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: string;
    } | null;
    throw new DevAuthError(
      res.status,
      body?.error ?? `dev_auth_failed_${res.status}`,
    );
  }
  return (await res.json()) as DevAuthClinicianResponse;
}

// Clears the session cookie server-side.
export async function destroyClinicianSession(): Promise<void> {
  await fetch(`${API_BASE}/employee-api/v1/session/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...tenantHeader() },
    ...withCredentials,
  });
}

// Probe the session: a 200 means the cookie is valid and returns the claims;
// a 401/403 means signed out. Used on app mount to rehydrate auth state
// without ever reading the JWT.
export async function fetchClinicianMe(): Promise<ClinicianClaims | null> {
  const res = await fetch(`${API_BASE}/employee-api/v1/me`, {
    headers: { ...tenantHeader() },
    ...withCredentials,
  });
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error(`Session probe failed: ${res.status}`);
  // TES-281: runtime-validate the claims against the shared contract —
  // a malformed /me body (or a role outside the shared vocabulary) fails
  // loudly here instead of leaking a bogus shape into auth state.
  return clinicianMeResponseSchema.parse(await res.json());
}
